import React, { useEffect, useState } from "react";
import { CalendarDays, Clock, Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Calendar } from "../ui/calendar";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { useUser } from "../../context/UserContext";
import { fetchBookingConfig, fetchBookingSlots, createBooking } from "../../lib/api";
import { AddToCalendar } from "../common/AddToCalendar";

const APPT_TYPES = ["Free Consultation", "Credit Review", "Financial Planning", "Follow-up"];

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BookAppointmentModal({ open, onOpenChange, onBooked }) {
  const { token } = useUser();
  const [config, setConfig] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [type, setType] = useState(APPT_TYPES[0]);
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      fetchBookingConfig().then(setConfig).catch(() => {});
      setDate(null); setSlots([]); setTime(""); setNotes(""); setType(APPT_TYPES[0]); setDone(false);
    }
  }, [open]);

  const onPickDate = async (d) => {
    setDate(d);
    setTime("");
    if (!d) return;
    setLoadingSlots(true);
    try {
      const res = await fetchBookingSlots(token, toISO(d));
      let s = res.available ? res.slots : [];
      // Hide times that have already passed when booking for today.
      if (toISO(d) === toISO(new Date())) {
        const now = new Date();
        const cur = now.getHours() * 60 + now.getMinutes();
        s = s.map((slot) => {
          const [h, m] = slot.time.split(":").map(Number);
          return h * 60 + m <= cur ? { ...slot, available: false } : slot;
        });
      }
      setSlots(s);
    } catch (e) {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const disabledDays = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (config && !config.days.includes(d.getDay())) return true;
    return false;
  };

  const confirm = async () => {
    if (!date || !time) {
      toast.error("Please pick a date and time.");
      return;
    }
    setBooking(true);
    try {
      await createBooking(token, { date: toISO(date), time, type, notes });
      setDone(true);
      toast.success("Appointment booked!");
      onBooked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book that slot.");
    } finally {
      setBooking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="book-appointment-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-navy flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-gold-dark" /> Book an Appointment
          </DialogTitle>
          <DialogDescription>Choose an available day and time that works for you.</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-10 flex flex-col items-center text-center" data-testid="booking-success">
            <CheckCircle2 className="h-14 w-14 text-green-500" />
            <h3 className="mt-3 font-serif text-xl font-bold text-navy">You're booked!</h3>
            <p className="mt-1 text-gray-500 text-sm">
              {type} on {date && date.toLocaleDateString()} at {time}. We'll confirm shortly.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row items-center gap-2">
              <AddToCalendar appt={{ date: date ? toISO(date) : "", time, type, notes }} />
              <Button onClick={() => onOpenChange(false)} className="bg-navy hover:bg-navy-light text-white" data-testid="booking-close-button">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={onPickDate}
                disabled={disabledDays}
                className="rounded-lg border border-gray-100"
              />
              <p className="text-xs text-gray-400 mt-2">
                Available days are highlighted; weekends/closed days are disabled.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> Available times
                </label>
                {!date ? (
                  <p className="text-sm text-gray-400 mt-2">Select a date to see open times.</p>
                ) : loadingSlots ? (
                  <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-gray-400 mt-2">No times available on this day.</p>
                ) : (
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="time-slots">
                    {slots.map((s) => (
                      <button
                        key={s.time}
                        disabled={!s.available}
                        onClick={() => setTime(s.time)}
                        className={`px-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                          time === s.time
                            ? "bg-navy text-white"
                            : s.available
                            ? "bg-cream text-navy hover:bg-gold/20"
                            : "bg-gray-100 text-gray-300 line-through cursor-not-allowed"
                        }`}
                        data-testid={`slot-${s.time}`}
                      >
                        {s.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Appointment type</label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="mt-1.5" data-testid="booking-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Notes (optional)</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" placeholder="Anything you'd like us to know?" data-testid="booking-notes-input" />
              </div>

              <Button onClick={confirm} disabled={booking || !date || !time} className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold" data-testid="confirm-booking-button">
                {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Booking"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
